import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { Box } from '@mui/material';
import {
    createVideoToolbarHandler,
    defaultQuillFormats,
    defaultQuillToolbar,
    matchIframeNode,
    matchMediaTextNode,
} from '../utils/quillMedia';

// Global map to track initialized editors by element
const initializedEditors = new WeakMap();

const QuillEditor = forwardRef(({ value, onChange, onBlur, placeholder, readOnly, modules, formats, theme = 'snow' }, ref) => {
    const editorRef = useRef(null);
    const quillInstanceRef = useRef(null);

    // Memoize onChange and onBlur callbacks to avoid recreating listeners
    const onChangeRef = useRef(onChange);
    const onBlurRef = useRef(onBlur);
    useEffect(() => {
        onChangeRef.current = onChange;
        onBlurRef.current = onBlur;
    }, [onChange, onBlur]);

    useEffect(() => {
        if (!editorRef.current) return;
        let quill = null;

        // Check if this element has already been initialized using WeakMap
        if (initializedEditors.has(editorRef.current)) {
            quill = initializedEditors.get(editorRef.current);
        }

        // Check if Quill already initialized on this element (Quill stores instance on element)
        if (!quill && editorRef.current.__quill) {
            quill = editorRef.current.__quill;
            initializedEditors.set(editorRef.current, quill);
        }

        // Check if container already has Quill classes (meaning it's already initialized)
        const existingToolbar = editorRef.current.querySelector('.ql-toolbar');
        const existingContainer = editorRef.current.querySelector('.ql-container');
        if (!quill && (existingToolbar || existingContainer)) {
            if (editorRef.current.__quill) {
                quill = editorRef.current.__quill;
                initializedEditors.set(editorRef.current, quill);
            }
        }

        // Clean up duplicates if any
        const toolbars = editorRef.current.querySelectorAll('.ql-toolbar');
        const containers = editorRef.current.querySelectorAll('.ql-container');
        if (toolbars.length > 1 || containers.length > 1) {
            for (let i = 1; i < toolbars.length; i++) {
                toolbars[i].remove();
            }
            for (let i = 0; i < containers.length - 1; i++) {
                containers[i].remove();
            }
        }

        if (!quill) {
            // Clear any existing content in the container
            editorRef.current.innerHTML = '';

            const resolvedModules = modules || {
                toolbar: {
                    container: defaultQuillToolbar,
                    handlers: {},
                },
                clipboard: {
                    matchers: [
                        [Node.TEXT_NODE, matchMediaTextNode],
                        ['IFRAME', matchIframeNode],
                    ],
                },
            };

            // Initialize Quill only once
            quill = new Quill(editorRef.current, {
                theme: theme,
                placeholder: placeholder || 'Compose your content...',
                readOnly: readOnly || false,
                modules: resolvedModules,
                formats: formats || defaultQuillFormats,
            });

            // Wire video handler after init so it can use this quill instance
            if (!modules?.toolbar?.handlers?.video) {
                const toolbar = quill.getModule('toolbar');
                if (toolbar) {
                    toolbar.addHandler('video', createVideoToolbarHandler(quill));
                }
            }

            initializedEditors.set(editorRef.current, quill);
        }

        quillInstanceRef.current = quill;

        // Set initial value
        const initialValue = value || '<p><br></p>';
        if (quill.root.innerHTML !== initialValue) {
            quill.root.innerHTML = initialValue;
        }

        // Listen for text changes
        const handleTextChange = () => {
            if (!quillInstanceRef.current) return;
            const html = quillInstanceRef.current.root.innerHTML;
            // Check if content is empty (only whitespace or empty p tags)
            const isEmpty = html === '<p><br></p>' || html.trim() === '' || html === '<p></p>';
            // Always call onChange with the actual HTML content, even if empty
            // Let the form validation handle empty checks
            if (onChangeRef.current) {
                onChangeRef.current(isEmpty ? '' : html);
            }
        };

        quill.on('text-change', handleTextChange);

        // Handle blur event to ensure value is saved when editor loses focus
        const handleBlur = () => {
            if (!quillInstanceRef.current) return;
            const html = quillInstanceRef.current.root.innerHTML;
            const isEmpty = html === '<p><br></p>' || html.trim() === '' || html === '<p></p>';
            
            // Update value on blur to ensure form has latest content
            if (onChangeRef.current) {
                onChangeRef.current(isEmpty ? '' : html);
            }
            
            // Call onBlur if provided
            if (onBlurRef.current) {
                onBlurRef.current();
            }
        };

        const handleSelectionChange = (range, oldRange, source) => {
            // When selection is cleared (editor loses focus), trigger blur
            if (!range && oldRange) {
                handleBlur();
            }
        };
        quill.on('selection-change', handleSelectionChange);

        // Also listen to blur on the editor element
        const editorElement = quill.root;
        editorElement.addEventListener('blur', handleBlur);

        return () => {
            // Cleanup: remove event listeners
            if (quillInstanceRef.current) {
                quillInstanceRef.current.off('text-change', handleTextChange);
                quillInstanceRef.current.off('selection-change', handleSelectionChange);
            }
            if (editorElement) {
                editorElement.removeEventListener('blur', handleBlur);
            }
            // Note: We don't destroy the Quill instance here as it's attached to the DOM
            // Let React handle the DOM cleanup
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount only

    // Cleanup effect to remove any duplicate toolbars that might have been created
    useEffect(() => {
        if (!editorRef.current) return;

        const cleanupDuplicates = () => {
            if (!editorRef.current) return;
            const toolbars = editorRef.current.querySelectorAll('.ql-toolbar');
            const containers = editorRef.current.querySelectorAll('.ql-container');
            
            if (toolbars.length > 1 || containers.length > 1) {
                // Keep only the first toolbar and last container
                for (let i = 1; i < toolbars.length; i++) {
                    toolbars[i].remove();
                }
                for (let i = 0; i < containers.length - 1; i++) {
                    containers[i].remove();
                }
            }
        };

        // Run cleanup immediately and after a short delay to catch any async duplicates
        cleanupDuplicates();
        const timeoutId = setTimeout(cleanupDuplicates, 100);

        return () => {
            clearTimeout(timeoutId);
        };
    }, []);

    // Update content when value prop changes (external updates)
    useEffect(() => {
        if (quillInstanceRef.current && value !== undefined) {
            const currentContent = quillInstanceRef.current.root.innerHTML;
            // Normalize empty values
            const normalizedCurrent = currentContent === '<p><br></p>' ? '' : currentContent;
            const normalizedValue = value === '<p><br></p>' || !value ? '' : value;
            
            // Only update if the content is different to avoid cursor jumping
            if (normalizedCurrent !== normalizedValue) {
                const selection = quillInstanceRef.current.getSelection();
                quillInstanceRef.current.root.innerHTML = normalizedValue || '<p><br></p>';
                // Try to restore selection after a brief delay
                if (selection) {
                    setTimeout(() => {
                        if (quillInstanceRef.current) {
                            quillInstanceRef.current.setSelection(selection);
                        }
                    }, 0);
                }
            }
        }
    }, [value]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
        getEditor: () => quillInstanceRef.current,
        getContent: () => {
            if (!quillInstanceRef.current) return '';
            const html = quillInstanceRef.current.root.innerHTML;
            const isEmpty = html === '<p><br></p>' || html.trim() === '' || html === '<p></p>';
            return isEmpty ? '' : html;
        },
        setContent: (content) => {
            if (quillInstanceRef.current) {
                quillInstanceRef.current.root.innerHTML = content || '<p><br></p>';
            }
        }
    }));

    return (
        <Box
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                '& .ql-container': {
                    minHeight: '200px',
                    maxHeight: '360px',
                    overflow: 'auto',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    borderBottomLeftRadius: '4px',
                    borderBottomRightRadius: '4px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderTop: 'none'
                },
                '& .ql-editor': {
                    minHeight: '200px',
                    maxHeight: '340px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap'
                },
                '& .ql-editor .ql-video': {
                    display: 'block',
                    width: '100%',
                    maxWidth: '100%',
                    aspectRatio: '16 / 9',
                    height: 'auto',
                    border: 0,
                    borderRadius: '8px',
                    margin: '12px 0',
                    backgroundColor: '#000'
                },
                '& .ql-toolbar': {
                    borderTopLeftRadius: '4px',
                    borderTopRightRadius: '4px',
                    backgroundColor: '#fafafa',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderBottom: 'none'
                },
                '& .ql-editor.ql-blank::before': {
                    fontStyle: 'normal',
                    color: 'rgba(0, 0, 0, 0.6)'
                }
            }}
        >
            <Box 
                ref={editorRef}
                sx={{
                    maxWidth: '100%',
                    // Hide duplicate toolbars and containers
                    '& > .ql-toolbar ~ .ql-toolbar': {
                        display: 'none !important'
                    },
                    '& > .ql-container ~ .ql-container': {
                        display: 'none !important'
                    },
                    // Ensure only first toolbar and last container are visible
                    '& > .ql-toolbar:first-of-type': {
                        display: 'block !important'
                    },
                    '& > .ql-container:last-of-type': {
                        display: 'block !important'
                    }
                }}
            />
        </Box>
    );
});

QuillEditor.displayName = 'QuillEditor';

export default QuillEditor;

